import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { adminUpdateProperty, createProperty, getProperty, imageUrl, updateProperty, uploadImages } from '../api';
import { useAuth } from '../auth';
import SpecificationsEditor, { specificationsPayload } from '../components/SpecificationsEditor';
import AddressAutocomplete from '../components/AddressAutocomplete';
import FormWizard from '../components/FormWizard';
import BlockedDatesFields, { sanitizeBlockedDates } from '../components/BlockedDatesFields';
import InventoryPanel from '../components/InventoryPanel';
import { saveBlockedDates } from '../api';
import { PROPERTY_CATEGORIES } from '../listingOptions';

// Caps how many photos one listing can carry in the gallery - the upload endpoint itself also
// caps a single upload request at 6 files (rentalapi's routes/uploads.js), this is the total
// across multiple uploads for one listing.
const MAX_IMAGES = 15;
const MIN_IMAGES = 1;
const SITE_ADDRESS_KEYS = ['line1', 'line2', 'area', 'city', 'state', 'pincode', 'lat', 'lng', 'placeId', 'formatted'];

// The structured site address lives as flat attributes.site* keys (03_requirements_missing.md
// story 7) so the shared loose listingAttributesSchema accepts it with no change.
function siteAddressFromAttributes(a = {}) {
  return {
    line1: a.siteAddressLine1 || '', line2: a.siteAddressLine2 || '',
    area: a.siteArea || '', city: a.siteCity || '', state: a.siteState || '',
    pincode: a.sitePincode || '',
    lat: a.siteLat ?? null, lng: a.siteLng ?? null,
    placeId: a.sitePlaceId || '', formatted: a.siteFormatted || ''
  };
}

function siteAddressToAttributes(s = {}) {
  return {
    siteAddressLine1: s.line1 || '', siteAddressLine2: s.line2 || '',
    siteArea: s.area || '', siteCity: s.city || '', siteState: s.state || '',
    sitePincode: s.pincode || '',
    siteLat: s.lat == null || s.lat === '' ? undefined : Number(s.lat),
    siteLng: s.lng == null || s.lng === '' ? undefined : Number(s.lng),
    sitePlaceId: s.placeId || '', siteFormatted: s.formatted || ''
  };
}

const emptyForm = {
  name: '',
  description: '',
  price: '',
  priceUnit: 'month',
  stock: '',
  category: 'Apartment',
  location: '',
  images: [],
  bedrooms: '',
  bathrooms: '',
  areaSqft: '',
  amenities: '',
  ownerPhone: '',
  specifications: [],
  siteAddress: { line1: '', line2: '', area: '', city: '', state: '', pincode: '', lat: null, lng: null, placeId: '', formatted: '' },
  blockedDates: []
};

export function toFormState(property) {
  return {
    name: property.name || '',
    description: property.description || '',
    price: property.price ?? '',
    priceUnit: property.priceUnit || 'month',
    stock: property.stock ?? 1,
    category: property.category || 'Apartment',
    location: property.location || '',
    images: property.images || [],
    bedrooms: property.attributes?.bedrooms ?? '',
    bathrooms: property.attributes?.bathrooms ?? '',
    areaSqft: property.attributes?.areaSqft ?? '',
    amenities: (property.attributes?.amenities || []).join(', '),
    ownerPhone: property.attributes?.ownerPhone || '',
    specifications: property.specifications?.length ? property.specifications.map((s) => ({ label: s.label || '', value: s.value || '' })) : [],
    siteAddress: siteAddressFromAttributes(property.attributes || {}),
    blockedDates: Array.isArray(property.blockedDates)
      ? property.blockedDates.map((b) => ({
        from: b.from ? String(b.from).slice(0, 10) : '',
        to: b.to ? String(b.to).slice(0, 10) : '',
        kind: b.kind || 'other',
        reason: b.reason || ''
      }))
      : []
  };
}

export function toPayload(form) {
  return {
    name: form.name,
    description: form.description,
    price: Number(form.price),
    priceUnit: form.priceUnit,
    stock: Number(form.stock),
    category: form.category,
    location: form.location,
    images: form.images,
    attributes: {
      bedrooms: form.bedrooms === '' ? undefined : Number(form.bedrooms),
      bathrooms: form.bathrooms === '' ? undefined : Number(form.bathrooms),
      areaSqft: form.areaSqft === '' ? undefined : Number(form.areaSqft),
      amenities: form.amenities.split(',').map((s) => s.trim()).filter(Boolean),
      ownerPhone: form.ownerPhone,
      ...siteAddressToAttributes(form.siteAddress)
    },
    specifications: specificationsPayload(form)
  };
}

function ListingPreview({ form }) {
  return (
    <Card>
      {form.images?.[0] && (
        <Card.Img variant="top" src={imageUrl(form.images[0])} style={{ height: 200, objectFit: 'cover' }} alt={form.name} />
      )}
      <Card.Body>
        <Card.Title>{form.name || 'Untitled property'}</Card.Title>
        <div className="text-muted mb-2">{form.category} &middot; {form.location || 'No location'}</div>
        <h5>₹{form.price || 0}/{form.priceUnit}</h5>
        <p>{form.description}</p>
        <div className="small text-muted">
          {form.bedrooms && <span className="me-2">{form.bedrooms} bed</span>}
          {form.bathrooms && <span className="me-2">{form.bathrooms} bath</span>}
          {form.areaSqft && <span className="me-2">{form.areaSqft} sqft</span>}
        </div>
        <div className="small text-muted mt-2">{form.images.length} photo(s)</div>
      </Card.Body>
    </Card>
  );
}

export default function PropertyForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  // admin_real_estate_property_management.md - the same form serves the website owner editing any
  // listing; it then lands back on the admin dashboard instead of "My Properties".
  const isAdmin = user?.role === 'admin';
  const homePath = isAdmin ? '/admin' : '/owner';

  const [form, setForm] = useState({ ...emptyForm, ownerPhone: user?.phone || '' });
  const [loading, setLoading] = useState(isEdit);
  const [loaded, setLoaded] = useState(!isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    getProperty(id)
      .then((property) => { setForm(toFormState(property)); setLoaded(true); })
      .catch(() => setError('Could not load this property.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let the same file be re-selected later if removed then re-added
    if (files.length === 0) return;

    setUploadError('');
    setUploading(true);
    try {
      const urls = await uploadImages(files);
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls].slice(0, MAX_IMAGES) }));
    } catch (err) {
      setUploadError(err.response?.data?.msg || 'Unable to upload those images');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (form.images.length < MIN_IMAGES) {
      setError('Please add at least 1 photo before publishing.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = toPayload(form);
      let savedId = id;
      if (isEdit) await (isAdmin ? adminUpdateProperty(id, payload) : updateProperty(id, payload));
      else savedId = (await createProperty(payload))._id;
      // blockedDates has its own dedicated route (not part of the listing body).
      await saveBlockedDates(savedId, sanitizeBlockedDates(form.blockedDates)).catch(() => {});
      navigate(homePath); // hassle-free: land back on the dashboard instead of an empty confirmation page
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save property');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner animation="border" role="status" />;

  const photosStep = (
    <Form.Group className="mb-3">
      <Form.Label>Photos</Form.Label>{' '}
      <span className="text-muted small">
        {form.images.length} / {MAX_IMAGES} photos (minimum 1)
      </span>
      {uploadError && <Alert variant="danger" className="py-2">{uploadError}</Alert>}
      <div className="d-flex flex-wrap gap-2 mb-2">
        {form.images.map((src, index) => (
          <div key={src} className="position-relative">
            <img
              src={imageUrl(src)}
              alt={`${form.name || 'Property'} ${index + 1}`}
              style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6 }}
            />
            <Button
              type="button"
              variant="danger"
              size="sm"
              className="position-absolute p-0 d-flex align-items-center justify-content-center"
              style={{ top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', lineHeight: 1 }}
              onClick={() => handleRemoveImage(index)}
              aria-label={`Remove photo ${index + 1}`}
            >
              ✕
            </Button>
          </div>
        ))}
        {uploading && (
          <div className="d-flex align-items-center justify-content-center" style={{ width: 90, height: 90 }}>
            <Spinner animation="border" size="sm" role="status" />
          </div>
        )}
      </div>
      <Form.Control
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        disabled={uploading || form.images.length >= MAX_IMAGES}
        onChange={handleFilesSelected}
      />
      <Form.Text muted>
        {form.images.length >= MAX_IMAGES
          ? `Maximum of ${MAX_IMAGES} photos reached - remove one to add another.`
          : `JPEG, PNG, WEBP, or GIF — resized and compressed automatically before upload. At least 1 photo is required.`}
      </Form.Text>
    </Form.Group>
  );

  const steps = [
    {
      key: 'basics',
      title: 'Basics',
      validate: (v) => (v.name.trim() ? null : { name: 'Title is required.' }),
      render: () => (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Title</Form.Label>
            <Form.Control required value={form.name} onChange={handleChange('name')} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.description} onChange={handleChange('description')} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Type</Form.Label>
            <Form.Select value={form.category} onChange={handleChange('category')}>
              {PROPERTY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </Form.Select>
          </Form.Group>
        </>
      )
    },
    {
      key: 'pricing',
      title: 'Pricing',
      validate: (v) => {
        if (v.price === '' || Number(v.price) < 0) return { price: 'Enter a rent of 0 or more.' };
        if (v.stock === '' || Number(v.stock) < 1) return { stock: 'Enter how many identical units you have (at least 1).' };
        return null;
      },
      render: () => (
        <>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Rent</Form.Label>
                <Form.Control type="number" min={0} required value={form.price} onChange={handleChange('price')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Per</Form.Label>
                <Form.Select value={form.priceUnit} onChange={handleChange('priceUnit')}>
                  <option value="month">Month</option>
                  <option value="week">Week</option>
                  <option value="night">Night</option>
                  <option value="day">Day</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {/* v8 stock_and_availability - how many identical units this listing represents
                  (e.g. several flats of the same layout). Most properties are one-of-a-kind, so
                  this stays 1 unless you're listing a repeated unit. */}
              <Form.Group className="mb-2">
                <Form.Label>Number of identical units</Form.Label>
                <Form.Control type="number" min={1} required value={form.stock} onChange={handleChange('stock')} />
                <Form.Text muted>Leave at 1 unless you have more than one identical unit to rent/visit.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          {isEdit && (
            <>
              <BlockedDatesFields
                value={form.blockedDates}
                onChange={(blockedDates) => setForm((prev) => ({ ...prev, blockedDates }))}
              />
              <InventoryPanel itemId={id} />
            </>
          )}
        </>
      )
    },
    {
      key: 'specs',
      title: 'Details & specs',
      validate: (v) => (v.ownerPhone.trim() ? null : { ownerPhone: 'A contact phone is required.' }),
      render: () => (
        <>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Bedrooms</Form.Label>
                <Form.Control type="number" min={0} value={form.bedrooms} onChange={handleChange('bedrooms')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Bathrooms</Form.Label>
                <Form.Control type="number" min={0} value={form.bathrooms} onChange={handleChange('bathrooms')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Area (sqft)</Form.Label>
                <Form.Control type="number" min={0} value={form.areaSqft} onChange={handleChange('areaSqft')} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label>Amenities (comma-separated)</Form.Label>
            <Form.Control placeholder="Parking, Gym, Swimming Pool" value={form.amenities} onChange={handleChange('amenities')} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Contact phone</Form.Label>
            <Form.Control required value={form.ownerPhone} onChange={handleChange('ownerPhone')} />
            <Form.Text muted>Hidden from visitors until they log in.</Form.Text>
          </Form.Group>
          <SpecificationsEditor form={form} setForm={setForm} />
          <Form.Text muted className="d-block mb-3" style={{ marginTop: -8 }}>
            Add rows visitors care about — Facing, Floor, Furnishing, Age of construction, Parking, Possession date.
          </Form.Text>
        </>
      )
    },
    {
      key: 'location',
      title: 'Location',
      validate: (v) => (v.location.trim() ? null : { location: 'Location is required.' }),
      render: () => (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Location (city / area)</Form.Label>
            <Form.Control required value={form.location} onChange={handleChange('location')} />
          </Form.Group>

          {/* 03_requirements_missing.md story 7 - structured site address, shown to visitors on a
              map (ItemDetail) and given to the assigned agent for directions (AgentDashboard). */}
          <Form.Group className="mb-3">
            <Form.Label>Site address</Form.Label>
            <Form.Text muted className="d-block mb-2">
              Where the property actually is. Used for the map on the listing and for the visiting agent.
            </Form.Text>
            <AddressAutocomplete
              showState
              idPrefix="site-addr"
              value={SITE_ADDRESS_KEYS.reduce((o, k) => ({ ...o, [k]: form.siteAddress[k] }), {})}
              onChange={(a) => setForm((prev) => ({ ...prev, siteAddress: { ...prev.siteAddress, ...a } }))}
            />
          </Form.Group>
        </>
      )
    },
    {
      key: 'photos',
      title: 'Photos & review',
      validate: (v) => (v.images.length >= MIN_IMAGES ? null : { images: 'Add at least 1 photo to publish.' }),
      render: () => (
        <>
          {photosStep}
          <hr />
          <h6>Review</h6>
          <dl className="row small mb-0">
            <dt className="col-sm-3">Title</dt><dd className="col-sm-9">{form.name || '—'}</dd>
            <dt className="col-sm-3">Type</dt><dd className="col-sm-9">{form.category}</dd>
            <dt className="col-sm-3">Rent</dt><dd className="col-sm-9">₹{form.price || 0}/{form.priceUnit}</dd>
            <dt className="col-sm-3">Units</dt><dd className="col-sm-9">{form.stock || '—'}</dd>
            <dt className="col-sm-3">Location</dt><dd className="col-sm-9">{form.location || '—'}</dd>
            <dt className="col-sm-3">Photos</dt><dd className="col-sm-9">{form.images.length}</dd>
          </dl>
          <p className="text-muted small mt-2">Use “Preview” to see the listing card, then submit.</p>
        </>
      )
    }
  ];

  return (
    <Card>
      <Card.Body>
        <Card.Title as="h3" className="mb-3">
          {isEdit ? 'Edit property' : 'List a new property'}
        </Card.Title>
        <FormWizard
          steps={steps}
          values={form}
          onChange={setForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={isEdit ? 'Save changes' : 'List property'}
          error={error}
          startVisitedAll={isEdit && loaded}
          renderPreview={(v) => <ListingPreview form={v} />}
        />
      </Card.Body>
    </Card>
  );
}
